import { Box } from '@mui/material';
import {
  getBankAccountsForNominee,
  getDocumentAvailablesForPaymentRequest,
} from 'lib/api';
import {
  createPaymentRequest,
  createPaymentRequestCash,
  markDocumentsAsPaid,
} from 'lib/api/payments';
import CustomStepper, { StepData } from 'lib/common/Stepper';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import { showAlert } from 'lib/functions/utils';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import NonNomineeDialog from './components/NonNomineeDialog';
import StepCashPayment from './components/StepCashPayment';
import StepConfigurePayment from './components/StepConfigurePayment';
import StepSelectDocuments from './components/StepSelectDocuments';
import {
  getDisplayAmount,
  IBankAccount,
  IDocumentStore,
  IPayment,
  IPaymentRequest,
  PaymentType,
} from './types';

const STEPS: StepData[] = [{}, {}];

export interface PayoutDocumentsProps {
  setOpen: (open: boolean) => void;
  setSuccessOpen: (success: string) => void;
  setFailureOpen: (failure: string) => void;
  setUp: () => Promise<void>;
}

const EMPTY_DOCUMENT_STORE: IDocumentStore = {
  cashDocuments: [],
  creditDocuments: [],
  receiptFeeDocuments: [],
};

const PayoutDocuments: React.FC<PayoutDocumentsProps> = ({
  setOpen,
  setSuccessOpen,
  setFailureOpen,
  setUp,
}) => {
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  // Paso 0: selección de documentos
  const [documents, setDocuments] =
    useState<IDocumentStore>(EMPTY_DOCUMENT_STORE);
  const [filteredDocuments, setFilteredDocuments] = useState(
    EMPTY_DOCUMENT_STORE.creditDocuments,
  );
  const [selectedDocuments, setSelectedDocuments] = useState<number[]>([]);
  const [paymentType, setPaymentType] =
    useState<PaymentType>('creditDocuments');

  // Paso 1 (crédito): configuración de nómina
  const [destinyAccounts, setDestinyAccounts] = useState<IBankAccount[]>([]);
  const [originAccounts, setOriginAccounts] = useState<IBankAccount[]>([]);
  const [payments, setPayments] = useState<IPayment[]>([]);
  const [selectedRequest, setSelectedRequest] =
    useState<IPaymentRequest | null>(null);

  // Paso 1 (efectivo): formulario de pago en efectivo
  const [cashBankAccountOriginId, setCashBankAccountOriginId] = useState<
    number | null
  >(null);
  const [cashForceExecutedAt, setCashForceExecutedAt] = useState(false);
  const [cashExecutedAt, setCashExecutedAt] = useState('');

  // Diálogos auxiliares
  const [openNonNominee, setOpenNonNominee] = useState(false);

  useEffect(() => {
    const fetchDocuments = async () => {
      setLoading(true);
      try {
        const data = await getDocumentAvailablesForPaymentRequest(dispatch);
        setDocuments(data);
        setFilteredDocuments(data[paymentType]);
      } catch {
        showAlert(dispatch, 'error', 'Error al cargar los documentos');
      }
      setLoading(false);
    };
    void fetchDocuments();
  }, []);

  useEffect(() => {
    setFilteredDocuments(documents[paymentType]);
  }, [paymentType, documents]);

  const handlePaymentTypeChange = (type: PaymentType) => {
    setPaymentType(type);
    setSelectedDocuments([]);
    setPayments([]);
    setDestinyAccounts([]);
    setOriginAccounts([]);
    setSelectedRequest(null);
    setCashBankAccountOriginId(null);
    setCashForceExecutedAt(false);
    setCashExecutedAt('');
  };

  const getStakeholderBusinessIdentifier = (): string => {
    const firstDoc = documents[paymentType].find(
      (doc) => doc.id === selectedDocuments[0],
    );
    return firstDoc?.businessIdentifier ?? '';
  };

  const buildPaymentRequest = (): IPaymentRequest => {
    const businessIdentifier = getStakeholderBusinessIdentifier();
    const firstDoc = documents[paymentType].find(
      (doc) => doc.businessIdentifier === businessIdentifier,
    );
    const selectedDocs = documents[paymentType].filter((doc) =>
      selectedDocuments.includes(doc.id),
    );
    return {
      businessName: firstDoc?.businessName ?? '',
      amount: selectedDocs.reduce(
        (acc, doc) => acc + (doc.amountToPay ?? getDisplayAmount(doc)),
        0,
      ),
      documents: selectedDocs,
    };
  };

  const fetchPaymentAccounts = async (): Promise<boolean> => {
    setLoading(true);
    try {
      const businessIdentifier = getStakeholderBusinessIdentifier();
      const accounts = await getBankAccountsForNominee(
        dispatch,
        businessIdentifier,
      );
      if (accounts) {
        setDestinyAccounts(accounts.businessBankAccounts);
        setOriginAccounts(accounts.ownBankAccounts);
        setLoading(false);
        return true;
      }
    } catch {
      setFailureOpen('Error al obtener las cuentas');
    }
    setLoading(false);
    return false;
  };

  const handleNextStep = async () => {
    const accountsLoaded = await fetchPaymentAccounts();
    if (!accountsLoaded) return;

    if (paymentType === 'cashDocuments') {
      setActiveStep(1);
      return;
    }
    setSelectedRequest(buildPaymentRequest());
    setActiveStep(1);
  };

  const handleSubmitCredit = async () => {
    setLoading(true);
    try {
      await createPaymentRequest(dispatch, {
        payments,
        documentsToPay: selectedDocuments,
        executedAt: null,
      });
      await setUp();
      setSuccessOpen('Pago configurado con éxito');
      setOpen(false);
    } catch {
      setFailureOpen('Error al configurar el pago');
    }
    setLoading(false);
  };

  const handleSubmitCash = async () => {
    setLoading(true);
    try {
      await createPaymentRequestCash(dispatch, {
        documentsToPayIds: selectedDocuments,
        bankAccountOriginId: cashBankAccountOriginId,
        ...(cashForceExecutedAt && cashExecutedAt
          ? { executedAt: cashExecutedAt }
          : {}),
      });
      await setUp();
      setSuccessOpen('Pago configurado con éxito');
      setOpen(false);
    } catch {
      setFailureOpen('Error al configurar el pago');
    }
    setLoading(false);
  };

  const handleMarkAsPaid = async () => {
    setLoading(true);
    try {
      await markDocumentsAsPaid(dispatch, {
        documentsToPay: selectedDocuments,
      });
      await setUp();
      setSuccessOpen('Documentos marcados como pagados');
      setOpenNonNominee(false);
      setOpen(false);
    } catch {
      setFailureOpen('Error al marcar como pagado');
    }
    setLoading(false);
  };

  const handleFinalizeStep = async () => {
    if (payments.length === 0) return;
    await handleSubmitCredit();
  };

  const primaryButton = useMemo(() => {
    if (activeStep === 0) {
      return { label: 'Siguiente', disabled: selectedDocuments.length === 0 };
    }
    if (paymentType === 'cashDocuments') {
      const isDateMissing = cashForceExecutedAt && !cashExecutedAt;
      return {
        label: 'Registrar pago',
        disabled: cashBankAccountOriginId === null || isDateMissing,
      };
    }
    return { label: 'Finalizar', disabled: payments.length === 0 };
  }, [
    activeStep,
    paymentType,
    selectedDocuments.length,
    payments.length,
    cashBankAccountOriginId,
  ]);

  const handlePrimaryAction = () => {
    if (activeStep === 0) return handleNextStep();
    if (paymentType === 'cashDocuments') return handleSubmitCash();
    return handleFinalizeStep();
  };

  if (loading) return <CenteredCircularProgress type="dialog" />;

  return (
    <div>
      <Box sx={{ px: 8, mb: 3 }}>
        <CustomStepper
          steps={STEPS}
          activeStep={activeStep}
          orientation="horizontal"
          showStepContent={false}
          showNavigationButtons={false}
          showResetButton={false}
          maxWidth="100%"
        />
      </Box>

      {activeStep === 0 && (
        <StepSelectDocuments
          documents={documents}
          filteredDocuments={filteredDocuments}
          setFilteredDocuments={setFilteredDocuments}
          paymentType={paymentType}
          onPaymentTypeChange={handlePaymentTypeChange}
          selectedDocuments={selectedDocuments}
          onSelectedDocumentsChange={setSelectedDocuments}
        />
      )}

      {activeStep === 1 && paymentType === 'cashDocuments' && (
        <StepCashPayment
          originAccounts={originAccounts}
          bankAccountOriginId={cashBankAccountOriginId}
          onBankAccountChange={setCashBankAccountOriginId}
          forceExecutedAt={cashForceExecutedAt}
          onForceExecutedAtChange={setCashForceExecutedAt}
          executedAt={cashExecutedAt}
          onExecutedAtChange={setCashExecutedAt}
        />
      )}

      {activeStep === 1 && paymentType !== 'cashDocuments' && (
        <StepConfigurePayment
          documents={documents}
          paymentType={paymentType}
          selectedDocuments={selectedDocuments}
          destinyAccounts={destinyAccounts}
          originAccounts={originAccounts}
          payments={payments}
          setPayments={setPayments}
          selectedRequest={selectedRequest}
          onOpenNonNominee={() => setOpenNonNominee(true)}
          onReloadAccounts={fetchPaymentAccounts}
        />
      )}

      <Box
        sx={{
          mt: 5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box sx={{ minWidth: 120 }} />
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
          <CustomButton
            color="primary"
            onClick={handlePrimaryAction}
            disabled={primaryButton.disabled}
          >
            {primaryButton.label}
          </CustomButton>
        </Box>
        <Box sx={{ minWidth: 120 }} />
      </Box>

      <NonNomineeDialog
        open={openNonNominee}
        onClose={() => setOpenNonNominee(false)}
        onConfirm={handleMarkAsPaid}
      />
    </div>
  );
};

export default PayoutDocuments;
